import cn from 'classnames'
import { useMemo, useState } from 'react'

import { Translation, useSafetyTranslation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Input } from 'core-components/library/inputs'

import { FINANCIAL_COCKPIT_TRANSLATION_KEYS } from '../../../../configs/translations'
import { isSameEmail } from '../../../../lib'
import {
  useAttentionTeamPbms,
  useReplaceAttentionPbm,
} from '../../hooks/useAttentionPbmReplacement'
import type { IAttentionStatusesReminder } from '../../types'

import styles from './AttentionPbmReplacement.module.scss'

const KEYS = FINANCIAL_COCKPIT_TRANSLATION_KEYS.attentionStatuses.replacement

export interface IAttentionPbmPickerPartner {
  partnerId: string
  partnerName: string
}

interface IAttentionPbmPickerProps {
  /** The PBM being replaced. */
  pbm: IAttentionStatusesReminder
  /** The partners the PBM leaves; more than one are listed to be picked from, all at first. */
  partners: IAttentionPbmPickerPartner[]
  /** PBMs the partners already have, who cannot take them over again. */
  assignedEmails?: string[]
  onBack?: () => void
  onDone: () => void
}

/**
 * Picks who of the manager's own PBMs takes the partners over, and asks for it. The change goes
 * through a request, so the picker says it applies once approved.
 */
export const AttentionPbmPicker = ({
  pbm,
  partners,
  assignedEmails = [],
  onBack,
  onDone,
}: IAttentionPbmPickerProps) => {
  const t = useSafetyTranslation()
  const { candidates, isLoading } = useAttentionTeamPbms(true)
  const { replace, isBusy } = useReplaceAttentionPbm()
  const [search, setSearch] = useState('')
  const [picked, setPicked] = useState<IAttentionStatusesReminder | null>(null)
  const [partnerIds, setPartnerIds] = useState(() => partners.map(({ partnerId }) => partnerId))
  const query = search.trim().toLowerCase()

  const options = useMemo(
    () =>
      candidates.filter(
        ({ email, name }) =>
          !isSameEmail(email, pbm.email) &&
          !assignedEmails.some((assigned) => isSameEmail(assigned, email)) &&
          (!query || name.toLowerCase().includes(query) || email.toLowerCase().includes(query))
      ),
    [assignedEmails, candidates, pbm.email, query]
  )

  const togglePartner = (partnerId: string, checked: boolean) =>
    setPartnerIds((current) =>
      checked ? [...current, partnerId] : current.filter((id) => id !== partnerId)
    )

  const handleSubmit = async () => {
    if (!picked || partnerIds.length === 0) {
      return
    }

    if (await replace({ partnerIds, from: pbm, to: picked })) {
      onDone()
    }
  }

  return (
    <div className={styles.picker}>
      <div className={styles.title}>
        {onBack && (
          <button type="button" className={styles.back} aria-label={t(KEYS.back)} onClick={onBack}>
            <i className="kbase kbase-arrow-left" aria-hidden="true" />
          </button>
        )}
        <Translation span text={KEYS.title} replaceParams={[{ key: '{name}', value: pbm.name }]} />
      </div>

      {partners.length > 1 && (
        <div className={styles.partners}>
          <span className={styles.caption}>
            <Translation span text={KEYS.partners} />
          </span>
          <div className={styles.partnerList}>
            {partners.map(({ partnerId, partnerName }) => (
              <Checkbox
                key={partnerId}
                size="sm"
                label={partnerName}
                checked={partnerIds.includes(partnerId)}
                onChange={(checked) => togglePartner(partnerId, checked)}
              />
            ))}
          </div>
        </div>
      )}

      <Input
        classes={{ root: styles.search }}
        value={search}
        onChange={setSearch}
        startContent={<i className="kbase kbase-search" />}
        placeholder={t(KEYS.search)}
      />

      <div className={styles.options} role="radiogroup" aria-label={t(KEYS.search)}>
        {!isLoading && options.length === 0 && (
          <p className={styles.empty}>
            <Translation span text={KEYS.noTeam} />
          </p>
        )}
        {options.map((option) => {
          const isPicked = picked?.email === option.email

          return (
            <button
              key={option.email}
              type="button"
              role="radio"
              aria-checked={isPicked}
              className={cn(styles.option, { [styles.optionPicked]: isPicked })}
              onClick={() => setPicked(option)}
            >
              <span className={styles.radio} aria-hidden="true" />
              <span className={styles.optionName}>{option.name}</span>
            </button>
          )
        })}
      </div>

      <p className={styles.note}>
        <Translation span text={KEYS.note} />
      </p>

      <div className={styles.footer}>
        <Button size="sm" variant="text" color="grey" onClick={onBack ?? onDone}>
          <Translation span text={KEYS.cancel} />
        </Button>
        <Button
          size="sm"
          variant="contained"
          loading={isBusy}
          disabled={!picked || partnerIds.length === 0}
          onClick={() => void handleSubmit()}
        >
          <Translation span text={KEYS.submit} />
        </Button>
      </div>
    </div>
  )
}

AttentionPbmPicker.displayName = 'AttentionPbmPicker'
